import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface RedactionModeContextType {
  /** Whether redaction mode is currently active */
  isEnabled: boolean;
  /** Toggle redaction mode on/off */
  toggle: () => void;
  /** Set redaction mode explicitly */
  setEnabled: (enabled: boolean) => void;
  /** Known structured PII strings (patient names, DOBs, NHS numbers) */
  structuredPii: Set<string>;
  /** Register structured PII from case data */
  registerStructuredPii: (values: string[]) => void;
  /** Clear structured PII (e.g. when navigating away from a case) */
  clearStructuredPii: () => void;
  /** Atomically replace structured PII set (avoids clear+register race) */
  replaceStructuredPii: (values: string[]) => void;
  /** Session-level flag: when true, PDF blurring is skipped */
  sourceDocsRevealed: boolean;
  /** Set the sourceDocsRevealed flag for the session */
  setSourceDocsRevealed: (val: boolean) => void;
}

const RedactionModeContext = createContext<RedactionModeContextType | undefined>(undefined);

const STORAGE_KEY = 'clinlexis_redaction_mode';

export const useRedactionMode = () => {
  const context = useContext(RedactionModeContext);
  if (context === undefined) {
    throw new Error('useRedactionMode must be used within a RedactionModeProvider');
  }
  return context;
};

interface RedactionModeProviderProps {
  children: ReactNode;
}

export const RedactionModeProvider: React.FC<RedactionModeProviderProps> = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    try {
      const val = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem('clinlexis_privacy_mode');
      return val === 'true';
    } catch {
      return false;
    }
  });

  const [sourceDocsRevealed, setSourceDocsRevealed] = useState(false);
  const [structuredPii, setStructuredPii] = useState<Set<string>>(new Set());

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isEnabled));
    } catch {
      // Ignore storage errors
    }
  }, [isEnabled]);

  // Reset sensitive in-memory state on logout to prevent PII leaking across accounts
  useEffect(() => {
    const handleLogout = () => {
      setStructuredPii(new Set());
      setSourceDocsRevealed(false);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Keyboard shortcut: Cmd+Shift+P / Ctrl+Shift+P
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'p') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(() => {
    setIsEnabled(prev => {
      const next = !prev;
      if (!next) setSourceDocsRevealed(false);
      return next;
    });
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) setSourceDocsRevealed(false);
  }, []);

  const registerStructuredPii = useCallback((values: string[]) => {
    setStructuredPii(prev => {
      const trimmed = values.map(v => v.trim()).filter(v => v.length > 1);
      const hasNew = trimmed.some(v => !prev.has(v));
      if (!hasNew) return prev; // no change — skip re-render
      const next = new Set(prev);
      trimmed.forEach(v => next.add(v));
      return next;
    });
  }, []);

  const clearStructuredPii = useCallback(() => {
    setStructuredPii(new Set());
  }, []);

  const replaceStructuredPii = useCallback((values: string[]) => {
    setStructuredPii(new Set(
      values.filter(v => v && v.trim().length > 1).map(v => v.trim())
    ));
  }, []);

  return (
    <RedactionModeContext.Provider value={{
      isEnabled,
      toggle,
      setEnabled,
      structuredPii,
      registerStructuredPii,
      clearStructuredPii,
      replaceStructuredPii,
      sourceDocsRevealed,
      setSourceDocsRevealed,
    }}>
      {children}
    </RedactionModeContext.Provider>
  );
};
